import type { QueryClient } from '@tanstack/react-query';
import type { DiscoveryEstimateBody } from '@discovr/types';

export const discoveryKeys = {
  all: ['discovery'] as const,
  campaign: (campaignId: string) => [...discoveryKeys.all, 'campaign', campaignId] as const,
  vettingCatalog: (campaignId: string) =>
    [...discoveryKeys.all, 'vetting-catalog', campaignId] as const,
  personaStarters: (campaignId: string, castVersion: string) =>
    [...discoveryKeys.all, 'persona-starters', campaignId, castVersion] as const,
  chats: (campaignId: string) => [...discoveryKeys.all, 'chats', campaignId] as const,
  personaChats: (campaignId: string, personaId: string | null) =>
    [...discoveryKeys.chats(campaignId), 'persona', personaId] as const,
  threads: (campaignId: string) => [...discoveryKeys.chats(campaignId), 'thread'] as const,
  chat: (campaignId: string, chatId: string) =>
    [...discoveryKeys.threads(campaignId), chatId] as const,
  turnTrace: (campaignId: string, turnId: string | null) =>
    [...discoveryKeys.all, 'turn-trace', campaignId, turnId] as const,
  searches: (campaignId: string) => [...discoveryKeys.all, 'searches', campaignId] as const,
  search: (campaignId: string, jobId: string | null) =>
    [...discoveryKeys.searches(campaignId), jobId] as const,
  results: (campaignId: string, jobId: string | null) =>
    [...discoveryKeys.search(campaignId, jobId), 'results'] as const,
  live: (campaignId: string, jobId: string | null) =>
    [...discoveryKeys.search(campaignId, jobId), 'live'] as const,
  rejected: (campaignId: string, jobId: string | null) =>
    [...discoveryKeys.search(campaignId, jobId), 'rejected'] as const,
  estimate: (campaignId: string, body: DiscoveryEstimateBody) =>
    [
      ...discoveryKeys.all,
      'estimate',
      campaignId,
      body.mode,
      body.target,
      body.seeds?.length ?? 0,
    ] as const,
  picks: (campaignId: string) => [...discoveryKeys.all, 'picks', campaignId] as const,
  picksPatch: (campaignId: string) => [...discoveryKeys.all, 'picks-patch', campaignId] as const,
  mediaLink: (campaignId: string, mediaKey: string) =>
    [...discoveryKeys.all, 'media-link', campaignId, mediaKey] as const,
};

export const invalidateSettledSearch = (
  queryClient: QueryClient,
  campaignId: string,
  jobId: string,
) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: discoveryKeys.search(campaignId, jobId) }),
    queryClient.invalidateQueries({ queryKey: discoveryKeys.campaign(campaignId) }),
  ]);

/** False when the campaign's latest read failed, so its `castVersion` is still the one a conflict just refused. */
export const campaignReadFresh = (queryClient: QueryClient, campaignId: string) =>
  queryClient.getQueryState(discoveryKeys.campaign(campaignId))?.status !== 'error';
